/**
 * DPE voisins — diagnostics de performance énergétique autour de la fiche.
 *
 * Source : ADEME Data Fair, dataset `dpe03existant` (DPE "nouveau format",
 * en vigueur depuis juillet 2021). CORS ✅, anonyme, pas de quota.
 * Endpoint : `data.ademe.fr/data-fair/api/v1/datasets/dpe03existant/lines`.
 *
 * Paramètre géo : `geo_distance=lng:lat:Rm` — attention, c'est `lng` en
 * premier, inverse de la convention lat/lng qu'on emploie partout ailleurs.
 * On centre la requête sur le point géocodé de la parcelle ; par défaut
 * l'API renvoie les résultats triés par distance croissante, donc la tranche
 * `size=200` capte systématiquement le voisinage immédiat sans `order_by`.
 *
 * Radius — piloté par le tier de la commune (`localityTier.ts`) :
 *   rural       2 km   — densité diffuse, on a besoin de volume pour
 *                        obtenir une distribution statistique
 *   petite      1,5 km
 *   moyenne     1 km
 *   grande      700 m
 *   métropole   500 m  — même à 500 m Paris central retourne ~5 k DPE,
 *                        la cap à 200 concentre donc les plus proches
 *
 * Distribution affichée : histogramme A→G calculé sur la totalité des
 * résultats renvoyés par l'API (le `size=200` est un cap, pas un sampling —
 * quand il y a < 200 DPE dans le rayon, on les a tous). Plus la médiane /
 * classe dominante pour un coup d'œil rapide.
 *
 * Note sur l'anonymisation : l'API expose l'adresse au bâtiment près
 * (`adresse_ban`) mais jamais le nom de l'occupant ni l'identifiant du
 * diagnostic — c'est déjà de l'open data au sens CNIL.
 */

import { useMemo } from "react";
import { coordKey, useEnvBlock } from "./shared";
import { useCommuneInfo, type LocalityTier } from "./localityTier";
import type { GeocodeResult } from "../types";

// Radius per locality tier, in meters. DPE density is tied to the urban
// fabric — rural needs a wider cast to reach statistical significance.
export const DPE_RADIUS_BY_TIER: Record<LocalityTier, number> = {
  rural: 2_000,
  petite: 1_500,
  moyenne: 1_000,
  grande: 700,
  metropole: 500,
};

// DPE moves faster than schools or shops — new diagnostics land daily.
// A 14-day stale window stays responsive without hammering the API on
// every fiche open.
const DPE_TTL_DAYS = 14;

const DPE_URL =
  "https://data.ademe.fr/data-fair/api/v1/datasets/dpe03existant/lines";

// Per-request cap. 200 is enough to draw a robust histogram and still
// serialize under ~30 KB in the cache doc (we also truncate the "recent"
// tail to 8 items before persisting — cf. `fetchDpe`).
const DPE_FETCH_SIZE = 200;

export type DpeClass = "A" | "B" | "C" | "D" | "E" | "F" | "G";

export type DpeItem = {
  id: string;
  classe: DpeClass;
  // `date_etablissement_dpe` — ISO YYYY-MM-DD.
  date: string;
  // `surface_habitable_logement` — m². Can be null in source when the
  // diagnostic is missing the field.
  surface_m2: number | null;
  address: string;
  lat: number;
  lng: number;
  distance_m: number;
};

export type DpeData = {
  center: { lat: number; lng: number };
  radius_m: number;
  tier: LocalityTier;
  fetchedAt: string;
  // Total diagnostics returned by the query — saturates at DPE_FETCH_SIZE.
  // A value of 200 indicates "200 ou plus" and should be rendered as such.
  total: number;
  // Histogram keyed by letter. Zero for empty buckets.
  distribution: Record<DpeClass, number>;
  // Median class across the total set — useful for a one-line subtitle.
  median: DpeClass | null;
  // 8 most recent diagnostics, date-desc. Keeps the Firestore doc lean
  // while giving the card enough rows to scroll through.
  recent: DpeItem[];
};

// ─── Raw API row shape (only the fields we select) ──────────────────────

type ApiRow = {
  _id?: string;
  etiquette_dpe?: string | null;
  date_etablissement_dpe?: string | null;
  surface_habitable_logement?: number | null;
  adresse_ban?: string | null;
  _geopoint?: string | null; // "lat,lng" as a string
};

const DPE_CLASSES: DpeClass[] = ["A", "B", "C", "D", "E", "F", "G"];

// ─── Fetch ──────────────────────────────────────────────────────────────

export async function fetchDpe(
  lat: number,
  lng: number,
  radiusM: number,
  tier: LocalityTier,
): Promise<DpeData> {
  // Note the lng-first order on `geo_distance` — inverse of our WKT
  // convention elsewhere. The ADEME Data Fair docs are explicit on this.
  const params = new URLSearchParams({
    geo_distance: `${lng}:${lat}:${radiusM}m`,
    size: String(DPE_FETCH_SIZE),
    select: [
      "_id",
      "etiquette_dpe",
      "date_etablissement_dpe",
      "surface_habitable_logement",
      "adresse_ban",
      "_geopoint",
    ].join(","),
  });

  const res = await fetch(`${DPE_URL}?${params.toString()}`, {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) {
    throw new Error(`ademe-dpe HTTP ${res.status}`);
  }
  const body = (await res.json()) as { results?: ApiRow[]; total?: number };
  const rows = Array.isArray(body.results) ? body.results : [];

  const items: DpeItem[] = [];
  const distribution: Record<DpeClass, number> = {
    A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, G: 0,
  };

  for (const r of rows) {
    const classe = normalizeClass(r.etiquette_dpe);
    if (!classe) continue;
    const pt = parseGeopoint(r._geopoint);
    if (!pt) continue;
    const distance_m = Math.round(
      haversineLite(lat, lng, pt.lat, pt.lng),
    );
    distribution[classe] += 1;
    items.push({
      id: r._id ?? `${pt.lat},${pt.lng}|${r.date_etablissement_dpe ?? ""}`,
      classe,
      date: r.date_etablissement_dpe ?? "",
      surface_m2:
        typeof r.surface_habitable_logement === "number"
          ? Math.round(r.surface_habitable_logement)
          : null,
      address: (r.adresse_ban ?? "").trim(),
      lat: pt.lat,
      lng: pt.lng,
      distance_m,
    });
  }

  // 8 most recent by date_etablissement_dpe desc. Stable secondary sort
  // on distance so ties render in a predictable order.
  const recent = [...items]
    .sort((a, b) => {
      if (a.date === b.date) return a.distance_m - b.distance_m;
      return a.date < b.date ? 1 : -1;
    })
    .slice(0, 8);

  return {
    center: { lat, lng },
    radius_m: radiusM,
    tier,
    fetchedAt: new Date().toISOString(),
    total: items.length,
    distribution,
    median: medianClass(distribution),
    recent,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────

function normalizeClass(raw: string | null | undefined): DpeClass | null {
  if (!raw) return null;
  const c = raw.trim().toUpperCase();
  return (DPE_CLASSES as string[]).includes(c) ? (c as DpeClass) : null;
}

function parseGeopoint(
  raw: string | null | undefined,
): { lat: number; lng: number } | null {
  if (!raw) return null;
  // Data Fair serializes `_geopoint` as "lat,lng".
  const [latStr, lngStr] = raw.split(",");
  const lat = Number(latStr);
  const lng = Number(lngStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * Inline Haversine — we duplicate the 6 lines from `shared.ts` to avoid
 * bundling the identical helper twice for the DPE code path (this module
 * is called in a tight loop over ≤ 200 rows).
 */
function haversineLite(
  lat1: number, lng1: number, lat2: number, lng2: number,
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * "Median DPE class" on an ordered categorical histogram — the class where
 * the cumulative count crosses 50 %. Returns null when the distribution is
 * empty. Used for the card subtitle so a single glance captures the feel of
 * the neighbourhood ("D dominante" vs "C dominante" vs "E dominante").
 */
function medianClass(dist: Record<DpeClass, number>): DpeClass | null {
  const total = DPE_CLASSES.reduce((s, c) => s + dist[c], 0);
  if (total === 0) return null;
  const half = total / 2;
  let cum = 0;
  for (const c of DPE_CLASSES) {
    cum += dist[c];
    if (cum >= half) return c;
  }
  return "G"; // unreachable unless all weights are zero, already guarded
}

// ─── Hook ───────────────────────────────────────────────────────────────

/**
 * `useDpe` — cache-first DPE block tied to the fiche's commune tier. Waits
 * for `useCommuneInfo` to resolve before firing so we don't fetch at the
 * fallback radius and then immediately invalidate.
 */
export function useDpe(
  ficheId: string | null,
  geocode: GeocodeResult | null,
) {
  const commune = useCommuneInfo(geocode?.insee ?? null);

  const opts = useMemo(() => {
    if (!ficheId || !geocode || !commune) return null;
    const radius = DPE_RADIUS_BY_TIER[commune.tier];
    return {
      ficheId,
      blockId: "dpe" as const,
      inputsKey: `v1|${coordKey(geocode.lat, geocode.lng)}|r${radius}|t${commune.tier}`,
      ttlDays: DPE_TTL_DAYS,
      fetcher: () => fetchDpe(geocode.lat, geocode.lng, radius, commune.tier),
    };
  }, [ficheId, geocode, commune]);

  return useEnvBlock<DpeData>(opts);
}
